import { apiFetch } from "./client";

export type Location = {
  id: string;
  code: string;
  name: string;
  kind: string;
  parentLocationId: string | null;
  siteId: string;
  depth: number;
  path: string;
  isSite: boolean;
  canHoldStock: boolean;
  isFulfillable: boolean;
  allocationPriority: number;
  isActive: boolean;
};

export type Variant = {
  id: string;
  sku: string;
  optionSummary: string;
  mrp: number | null;
  sellingPrice: number | null;
  costPrice: number | null;
  barcode: string | null;
  /** Whether receiving this produces individually labelled units. Changes what goods-in does. */
  isSerialised: boolean;
};

export type Product = {
  id: string;
  productCode: string;
  name: string;
  brand: string | null;
  categoryId: string;
  categoryName: string;
  optionNames: string[];
  variants: Variant[];
};

export type SerialisationPolicy = "None" | "Optional" | "Required";

/**
 * A node in the tenant's own product hierarchy.
 *
 * `depth` and `path` are computed by the API rather than derived here, so every client draws the
 * same tree and a dropdown can tell two sub-categories with the same name apart (ADR-0009).
 */
export type Category = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentCategoryId: string | null;
  depth: number;
  /** Ancestors and self, outermost first. */
  path: string[];
  serialisationPolicy: SerialisationPolicy;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
};

export type SaveLocation = {
  code: string;
  name: string;
  isFulfillable: boolean;
  allocationPriority: number;
};

export type SaveCategory = {
  code: string;
  name: string;
  description: string | null;
  parentCategoryId: string | null;
  serialisationPolicy: SerialisationPolicy;
  sortOrder: number;
};

/** How deep the tree may go. Mirrors the API, which is the authority and refuses anything past it. */
export const MAX_CATEGORY_DEPTH = 3;

export type ReceivedUnit = {
  stockUnitId: string;
  serial: string;
  sku: string;
  labelUrl: string;
};

export type GoodsReceipt = {
  goodsReceiptId: string;
  receiptNumber: string;
  totalQuantity: number;
  units: ReceivedUnit[];
};

export type StockUnit = {
  stockUnitId: string;
  serial: string;
  sku: string;
  bucket: string;
  siteCode: string;
  storageLocationCode: string | null;
  receivedAt: string;
  /**
   * Whether the unit is still stock the business holds.
   *
   * A unit that has left keeps the bucket it was last in, because there is no bucket for "gone".
   * So `bucket` alone would report a written-off label as "Available".
   */
  isInStock: boolean;
  /** What happened to it, if it has left. Null while it is in stock. */
  leftStockBecause: string | null;
  leftStockAt: string | null;
};

export type ReceiveLine = {
  productVariantId: string;
  quantity: number;
  unitCost: number;
  storageLocationId: string | null;
};

export const commerce = {
  locations: () => apiFetch<Location[]>("/locations"),

  sites: () => apiFetch<Location[]>("/locations/sites"),

  createSiteFromTemplate: (
    code: string,
    name: string,
    template: "RetailStore" | "Warehouse" | "Empty",
  ) =>
    apiFetch<Location[]>("/locations/sites/from-template", {
      method: "POST",
      body: { code, name, template },
    }),

  updateLocation: (id: string, body: SaveLocation) =>
    apiFetch<Location>(`/locations/${id}`, { method: "PUT", body }),

  setLocationActive: (id: string, isActive: boolean) =>
    apiFetch<Location>(`/locations/${id}/${isActive ? "reactivate" : "deactivate"}`, {
      method: "POST",
    }),

  deleteLocation: (id: string) => apiFetch<void>(`/locations/${id}`, { method: "DELETE" }),

  categories: () => apiFetch<Category[]>("/categories"),

  createCategory: (body: SaveCategory) =>
    apiFetch<Category>("/categories", { method: "POST", body }),

  updateCategory: (id: string, body: SaveCategory) =>
    apiFetch<Category>(`/categories/${id}`, { method: "PUT", body }),

  moveCategory: (id: string, parentCategoryId: string | null) =>
    apiFetch<Category>(`/categories/${id}/move`, {
      method: "POST",
      body: { parentCategoryId },
    }),

  setCategoryActive: (id: string, isActive: boolean) =>
    apiFetch<Category>(`/categories/${id}/${isActive ? "reactivate" : "deactivate"}`, {
      method: "POST",
    }),

  deleteCategory: (id: string) => apiFetch<void>(`/categories/${id}`, { method: "DELETE" }),

  products: (search?: string) =>
    apiFetch<Product[]>(`/products${search ? `?search=${encodeURIComponent(search)}` : ""}`),

  receive: (
    siteId: string,
    lines: ReceiveLine[],
    supplierReference: string | null,
    notes: string | null,
  ) =>
    apiFetch<GoodsReceipt>("/goods-receipts", {
      method: "POST",
      body: { siteId, lines, supplierReference, notes },
    }),

  putAway: (scanCode: string, storageLocationId: string) =>
    apiFetch<void>("/stock-units/put-away", {
      method: "POST",
      body: { scanCode, storageLocationId },
    }),

  unitBySerial: (serial: string) =>
    apiFetch<StockUnit>(`/stock-units/by-serial/${encodeURIComponent(serial)}`),
};
